import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './InvestmentsSection.css';
import { useAllTransactions, useAccounts, QUERY_KEYS } from '../TransactionService';
import { useQueryClient } from '@tanstack/react-query';
import api from '../api';

const InvestmentsSection = ({ dashboardId, onAddSIP }) => {
  const queryClient = useQueryClient();
  const { data: transactionsRaw } = useAllTransactions(dashboardId);
  const { data: accountsRaw } = useAccounts(dashboardId);
  const [activeSubTab, setActiveSubTab] = useState('stocks'); // stocks, mutual_funds, policies
  const [mfType, setMfType] = useState('sip'); // sip, lumpsum
  
  // Manual Form State
  const [manualAsset, setManualAsset] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [manualAccount, setManualAccount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Scheduled State
  const [scheduledInv, setScheduledInv] = useState([]);
  const [showSIPForm, setShowSIPForm] = useState(false);
  const [sipFundName, setSipFundName] = useState('');
  const [sipAmount, setSipAmount] = useState('');
  const [sipFrequency, setSipFrequency] = useState('monthly_fixed'); // weekly, biweekly, monthly_fixed
  const [sipDay, setSipDay] = useState(1);
  const [sipStartDate, setSipStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [sipAccount, setSipAccount] = useState('');
  const [sipRunTime, setSipRunTime] = useState('09:00');
  const [sipEndType, setSipEndType] = useState('never'); // never, count
  const [sipMaxOcc, setSipMaxOcc] = useState('');
  const [sipIsAlreadyRunning, setSipIsAlreadyRunning] = useState(false);
  const [sipAlreadyCount, setSipAlreadyCount] = useState('');
  const [sipSubmitting, setSipSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const transactions = useMemo(() => transactionsRaw || [], [transactionsRaw]);
  const accounts = useMemo(() => accountsRaw || [], [accountsRaw]);

  useEffect(() => {
    if (accounts.length > 0 && !manualAccount) {
      setManualAccount(accounts[0].id);
    }
  }, [accounts, manualAccount]);

  const fetchSched = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/scheduled-transactions?dashboard_id=${dashboardId}`);
      setScheduledInv(data.filter(s => s.is_investment || s.category === 'investments' || s.category === 'mutual_funds'));
    } catch (err) {
      console.error('Failed to fetch scheduled investments', err);
    }
  }, [dashboardId]);

  useEffect(() => {
    if (dashboardId) fetchSched();
  }, [dashboardId, fetchSched]);

  useEffect(() => {
    if (accounts.length > 0 && !sipAccount) {
      setSipAccount(accounts[0].id);
    }
  }, [accounts, sipAccount]);

  const handleManualSubmit = async () => {
    if (!manualAsset || !manualAmount) return;
    setSubmitting(true);
    try {
      const txData = {
        dashboard_id: dashboardId,
        account_id: manualAccount,
        amount: parseFloat(manualAmount),
        transaction_type: 'debit',
        category: 'investments',
        description: manualAsset,
        transaction_date: new Date().toISOString().slice(0, 10),
        is_investment: true
      };
      await api.post('/api/transactions', txData);
      
      // Clear and Refresh
      setManualAsset('');
      setManualAmount('');
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allTransactions(dashboardId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts(dashboardId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dashboardSummary(dashboardId) });
    } catch (err) {
      alert('Failed to record investment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSIPSubmit = async () => {
    if (!sipFundName || !sipAmount) return;
    setSipSubmitting(true);
    try {
      const payload = {
        dashboard_id: dashboardId,
        account_id: sipAccount,
        amount: parseFloat(sipAmount),
        transaction_type: 'debit',
        category: 'investments',
        description: sipFundName,
        recurrence_type: sipFrequency,
        recurrence_day_of_week: ['weekly', 'biweekly'].includes(sipFrequency) ? parseInt(sipDay) : null,
        recurrence_day_of_month: sipFrequency === 'monthly_fixed' ? parseInt(sipDay) : null,
        run_time: sipRunTime || '09:00',
        start_date: sipStartDate,
        max_occurrences: sipEndType === 'count' ? parseInt(sipMaxOcc) : null,
        is_investment: true
      };
      
      // If already running, we might want to record the "Already Value" as a past transaction 
      // but for now, we'll just store the schedule. 
      // Future improvement: Bulk record past SIP installments.
      
      if (editingId) {
        await api.patch(`/api/scheduled-transactions/${editingId}`, payload);
      } else {
        await api.post('/api/scheduled-transactions', payload);
      }
      
      // Reset
      setShowSIPForm(false);
      setEditingId(null);
      setSipFundName('');
      setSipAmount('');
      fetchSched();
    } catch (err) {
      alert('Failed to create SIP schedule');
    } finally {
      setSipSubmitting(false);
    }
  };

  const handleEdit = (sched) => {
    setSipFundName(sched.description || '');
    setSipAmount(sched.amount);
    setSipFrequency(sched.recurrence_type);
    setSipDay(sched.recurrence_day_of_month || sched.recurrence_day_of_week || 1);
    setSipStartDate(sched.start_date);
    setSipAccount(sched.account_id);
    setSipRunTime(sched.run_time || '09:00');
    setSipEndType(sched.max_occurrences ? 'count' : 'never');
    setSipMaxOcc(sched.max_occurrences || '');
    setEditingId(sched.id);
    setShowSIPForm(true);
  };

  const handleRunNow = async (schedId) => {
    try {
      await api.post(`/api/scheduled-transactions/${schedId}/run-now`);
      fetchSched();
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allTransactions(dashboardId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts(dashboardId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dashboardSummary(dashboardId) });
    } catch (err) {
      alert('Failed to execute schedule manually');
    }
  };

  // Filter for investment transactions
  const investmentTxs = transactions.filter(tx => tx.is_investment || tx.category === 'investments');

  const totalInvested = investmentTxs.reduce((s, t) => s + parseFloat(t.amount || 0), 0);
  const estValue = totalInvested * 1.08;

  return (
    <div className="inv-container">
      {/* Slim header — same shape as `.at-mode-picker-hd` and the new
          Lend & Borrow header so the section reads as part of one
          consistent flow. */}
      <div className="inv-header">
        <span className="inv-header-badge">📈 Investment Terminal</span>
        <h1 className="inv-header-title">Portfolio &amp; market insights</h1>
        <p className="inv-header-sub">
          Track stocks, mutual funds and policy payments — all the insights you need to grow your wealth.
        </p>
      </div>

      {/* Compact stats row */}
      <div className="inv-stats-row">
        <div className="inv-stat-card">
          <span className="inv-stat-label">Invested</span>
          <span className="inv-stat-value">₹{totalInvested.toLocaleString()}</span>
        </div>
        <div className="inv-stat-card">
          <span className="inv-stat-label">Est. Value</span>
          <span className="inv-stat-value">
            ₹{estValue.toLocaleString()}
            <small>+8%</small>
          </span>
        </div>
      </div>

      {/* Navigation Sub-Tabs */}
      <div className="inv-nav">
        {['stocks', 'mutual_funds', 'policies'].map(tab => (
          <button
            key={tab}
            className={`inv-nav-btn ${activeSubTab === tab ? 'active' : ''}`}
            onClick={() => setActiveSubTab(tab)}
          >
            {tab.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="inv-content-grid">
        
        {/* CASE 1: MUTUAL FUNDS */}
        {activeSubTab === 'mutual_funds' && (
          <>
            <div className="inv-panel inv-full-width" style={{marginBottom: 0, paddingBottom: 0, borderBottom: 'none', borderRadius: '24px 24px 0 0'}}>
              <div className="inv-type-toggle">
                <button className={`inv-toggle-btn ${mfType === 'sip' ? 'active' : ''}`} onClick={() => setMfType('sip')}>
                   🔄 Scheduled SIP
                </button>
                <button className={`inv-toggle-btn ${mfType === 'lumpsum' ? 'active' : ''}`} onClick={() => setMfType('lumpsum')}>
                   💰 One-time Lumpsum
                </button>
              </div>
            </div>

            {mfType === 'sip' ? (
              /* SIP Panel */
              <div className="inv-panel inv-full-width" style={{borderRadius: '0 0 24px 24px', borderTop: 'none'}}>
                <div className="inv-panel-hd">
                  <h3>SIP Schedules</h3>
                  <span className="inv-panel-sub">Automated Mutual Fund SIPs</span>
                </div>
                <div className="inv-sip-list">
                  {showSIPForm ? (
                    <div className="inv-sip-form">
                      <div className="inv-input-group">
                        <label>Fund Name</label>
                        <input type="text" placeholder="e.g. Parag Parikh Flexi Cap" value={sipFundName} onChange={e => setSipFundName(e.target.value)} />
                      </div>
                      <div className="inv-input-row">
                        <div className="inv-input-group">
                          <label>Amount (SIP)</label>
                          <input type="number" placeholder="0.00" value={sipAmount} onChange={e => setSipAmount(e.target.value)} />
                        </div>
                        <div className="inv-input-group">
                          <label>Frequency</label>
                          <select value={sipFrequency} onChange={e => setSipFrequency(e.target.value)}>
                            <option value="weekly">Weekly</option>
                            <option value="biweekly">Bi-weekly</option>
                            <option value="monthly_fixed">Monthly</option>
                          </select>
                        </div>
                      </div>
                      <div className="inv-input-row">
                          <div className="inv-input-group">
                            <label>{sipFrequency === 'monthly_fixed' ? 'Day of Month' : 'Day of Week (0-6)'}</label>
                            <input type="number" min="0" max={sipFrequency === 'monthly_fixed' ? 31 : 6} value={sipDay} onChange={e => setSipDay(e.target.value)} />
                          </div>
                          <div className="inv-input-group">
                            <label>Start Date</label>
                            <input type="date" value={sipStartDate} onChange={e => setSipStartDate(e.target.value)} />
                          </div>
                      </div>
                      <div className="inv-input-row">
                          <div className="inv-input-group">
                            <label>Debit From</label>
                            <select value={sipAccount} onChange={e => setSipAccount(e.target.value)}>
                              {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                            </select>
                          </div>
                          <div className="inv-input-group">
                            <label>Deduction Time</label>
                            <input type="time" value={sipRunTime} onChange={e => setSipRunTime(e.target.value)} />
                          </div>
                      </div>

                      <div className="inv-input-row" style={{alignItems: 'center', marginTop: 8}}>
                          <div className="inv-input-group">
                            <label>End Condition</label>
                            <select value={sipEndType} onChange={e => setSipEndType(e.target.value)}>
                              <option value="never">♾️ Never Ends</option>
                              <option value="count">🔢 Fixed Number of Times</option>
                            </select>
                          </div>
                          {sipEndType === 'count' && (
                            <div className="inv-input-group">
                              <label>Total Deductions</label>
                              <input type="number" placeholder="e.g. 60" value={sipMaxOcc} onChange={e => setSipMaxOcc(e.target.value)} />
                            </div>
                          )}
                      </div>

                      <div className="inv-checkbox-group" style={{marginTop: 12}}>
                        <label className="inv-check-label">
                          <input type="checkbox" checked={sipIsAlreadyRunning} onChange={e => setSipIsAlreadyRunning(e.target.checked)} />
                          Already running SIP? (Port existing data)
                        </label>
                      </div>

                      {sipIsAlreadyRunning && (
                        <div className="inv-input-row inv-already-box" style={{marginTop: 12, alignItems: 'center'}}>
                          <div className="inv-input-group">
                            <label>Installments Done</label>
                            <input type="number" placeholder="e.g. 12" value={sipAlreadyCount} onChange={e => setSipAlreadyCount(e.target.value)} />
                          </div>
                          <div className="inv-input-group">
                            <label>Calculated Value</label>
                            <div style={{fontSize: '1.1rem', fontWeight: 700, color: '#10b981', paddingTop: 8}}>
                               ₹{(parseFloat(sipAlreadyCount || 0) * parseFloat(sipAmount || 0)).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="inv-form-actions" style={{marginTop: 24, display: 'flex', gap: 12}}>
                        <button className="inv-submit-btn" style={{flex: 1, marginTop: 0}} onClick={handleSIPSubmit} disabled={sipSubmitting}>
                          {sipSubmitting ? (editingId ? 'Saving...' : 'Creating...') : (editingId ? 'Save Changes' : 'Start SIP')}
                        </button>
                        <button className="inv-cancel-btn" onClick={() => { setShowSIPForm(false); setEditingId(null); }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {scheduledInv.length > 0 ? scheduledInv.map(s => (
                      <div key={s.id} className="inv-sip-card">
                        <div className="inv-sip-ico">💎</div>
                        <div className="inv-sip-info">
                          <div className="inv-sip-name">{s.description || s.category}</div>
                          <div className="inv-sip-meta">Next: {s.next_run_date} · ₹{parseFloat(s.amount).toLocaleString()}</div>
                        </div>
                        <div className="inv-sip-actions">
                          <div style={{display: 'flex', gap: 6}}>
                            <button className="inv-run-now-btn" title="Execute Early" onClick={() => handleRunNow(s.id)}>
                              🚀 Log Now
                            </button>
                            <button className="inv-edit-btn" title="Edit Schedule" onClick={() => handleEdit(s)}>
                              ✏️
                            </button>
                          </div>
                          <div className="inv-sip-status">{s.is_active ? 'Active' : 'Paused'}</div>
                        </div>
                      </div>
                    )) : (
                        <div className="inv-sip-empty">No active SIP schedules found.</div>
                      )}
                      <button className="inv-add-sip-btn" onClick={() => setShowSIPForm(true)}>
                        + Create New Mutual Fund SIP
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              /* Lumpsum Panel */
              <div className="inv-panel inv-full-width" style={{borderRadius: '0 0 24px 24px', borderTop: 'none'}}>
                <div className="inv-panel-hd">
                  <h3>Lumpsum Investment</h3>
                  <span className="inv-panel-sub">Record one-time fund purchase</span>
                </div>
                <div className="inv-manual-form">
                  <div className="inv-input-group">
                    <label>Fund Name</label>
                    <input type="text" placeholder="e.g. Quant Small Cap" value={manualAsset} onChange={e => setManualAsset(e.target.value)} />
                  </div>
                  <div className="inv-input-row">
                    <div className="inv-input-group">
                      <label>Invested Amount</label>
                      <input type="number" placeholder="0.00" value={manualAmount} onChange={e => setManualAmount(e.target.value)} />
                    </div>
                    <div className="inv-input-group">
                      <label>Debit From</label>
                      <select value={manualAccount} onChange={e => setManualAccount(e.target.value)}>
                        {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <button className="inv-submit-btn" onClick={handleManualSubmit} disabled={submitting}>
                    {submitting ? 'Recording...' : 'Record Lumpsum Buy'}
                  </button>
                </div>

                <div className="inv-history" style={{marginTop: 32}}>
                  <h4>MF History</h4>
                  <div className="inv-hist-list">
                    {investmentTxs.slice(0, 5).map(tx => (
                      <div key={tx.id} className="inv-hist-row">
                        <span className="inv-hist-date">{new Date(tx.transaction_date).toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}</span>
                        <span className="inv-hist-desc">{tx.description || tx.category}</span>
                        <span className="inv-hist-amt">₹{parseFloat(tx.amount).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* CASE 2: STOCKS (Priority on Manual Entry) */}
        {activeSubTab === 'stocks' && (
          <>
            <div className="inv-panel">
              <div className="inv-panel-hd">
                <h3>Record Trade</h3>
                <span className="inv-panel-sub">Manual Stock Purchase or Sale</span>
              </div>
              <div className="inv-manual-form">
                <div className="inv-input-group">
                  <label>Ticker / Company Name</label>
                  <input type="text" placeholder="e.g. RELIANCE, TCS, AAPL..." value={manualAsset} onChange={e => setManualAsset(e.target.value)} />
                </div>
                <div className="inv-input-row">
                  <div className="inv-input-group">
                    <label>Amount Invested</label>
                    <input type="number" placeholder="0.00" value={manualAmount} onChange={e => setManualAmount(e.target.value)} />
                  </div>
                  <div className="inv-input-group">
                    <label>Brokerage Account</label>
                    <select value={manualAccount} onChange={e => setManualAccount(e.target.value)}>
                      {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                    </select>
                  </div>
                </div>
                <button className="inv-submit-btn" onClick={handleManualSubmit} disabled={submitting}>
                  {submitting ? 'Recording...' : 'Log Buy Order'}
                </button>
              </div>
            </div>
            
            <div className="inv-panel">
              <div className="inv-panel-hd">
                <h3>Stock History</h3>
                <span className="inv-panel-sub">Your recent market moves</span>
              </div>
              <div className="inv-history" style={{marginTop: 0}}>
                <div className="inv-hist-list">
                  {investmentTxs.slice(0, 8).map(tx => (
                    <div key={tx.id} className="inv-hist-row">
                      <span className="inv-hist-date">{new Date(tx.transaction_date).toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}</span>
                      <span className="inv-hist-desc">{tx.description || tx.category}</span>
                      <span className="inv-hist-amt">₹{parseFloat(tx.amount).toLocaleString()}</span>
                    </div>
                  ))}
                  {investmentTxs.length === 0 && <div className="inv-hist-empty">No trades recorded yet</div>}
                </div>
              </div>
            </div>
          </>
        )}

        {/* CASE 3: POLICIES (Mixed) */}
        {activeSubTab === 'policies' && (
          <>
            <div className="inv-panel">
               <div className="inv-panel-hd">
                <h3>New Policy</h3>
                <span className="inv-panel-sub">Record an insurance or policy payment</span>
              </div>
              <div className="inv-manual-form">
                <div className="inv-input-group">
                  <label>Policy Name</label>
                  <input type="text" placeholder="e.g. HDFC Life, LIC..." value={manualAsset} onChange={e => setManualAsset(e.target.value)} />
                </div>
                <div className="inv-input-row">
                  <div className="inv-input-group">
                    <label>Premium Amount</label>
                    <input type="number" placeholder="0.00" value={manualAmount} onChange={e => setManualAmount(e.target.value)} />
                  </div>
                  <div className="inv-input-group">
                    <label>Payment Mode</label>
                    <select value={manualAccount} onChange={e => setManualAccount(e.target.value)}>
                      {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                    </select>
                  </div>
                </div>
                <button className="inv-submit-btn" onClick={handleManualSubmit} disabled={submitting}>
                  Record Premium Payment
                </button>
              </div>
            </div>
            <div className="inv-panel">
               <div className="inv-panel-hd">
                <h3>Policy History</h3>
                <span className="inv-panel-sub">Past premium records</span>
              </div>
              <div className="inv-history" style={{marginTop: 0}}>
                <div className="inv-hist-list">
                  {investmentTxs.slice(0, 8).map(tx => (
                    <div key={tx.id} className="inv-hist-row">
                      <span className="inv-hist-date">{new Date(tx.transaction_date).toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}</span>
                      <span className="inv-hist-desc">{tx.description || tx.category}</span>
                      <span className="inv-hist-amt">₹{parseFloat(tx.amount).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default InvestmentsSection;
